import React, { useState } from 'react';
import {
  Target, Zap, Globe, Star, MapPin, ShieldCheck, UserCheck, 
  Rocket, Layers, ChevronDown, ChevronUp, ClipboardCheck, 
  Award, TrendingUp, Search, AlertTriangle, Sparkles, Lightbulb, 
  Edit3, CheckCircle, Plus, Check, HelpCircle
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAppContext } from '../../../core/App';

const ChangeCard = ({ title, oldText, newText, rationale }) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm mb-4 transition-all duration-200">
      <button 
        onClick={() => setIsOpen(!isOpen)}
        className="w-full bg-slate-50 px-4 py-3 border-b border-slate-200 flex justify-between items-center focus:outline-none hover:bg-slate-100"
      >
        <span className="text-xs font-black uppercase tracking-widest text-slate-600">{title}</span>
        <div className="flex items-center gap-2">
          <div className="flex gap-1 mr-2">
            <div className="w-2 h-2 rounded-full bg-red-400" />
            <div className="w-2 h-2 rounded-full bg-emerald-400" />
          </div>
          {isOpen ? <ChevronUp size={16} className="text-slate-400" /> : <ChevronDown size={16} className="text-slate-400" />}
        </div>
      </button>
      <AnimatePresence>
        {isOpen && (
          <motion.div 
            initial={{ height: 0, opacity: 0 }} 
            animate={{ height: 'auto', opacity: 1 }} 
            exit={{ height: 0, opacity: 0 }}
            className="overflow-hidden"
          >
            <div className="p-4 space-y-3">
              <p className="text-xs text-slate-400 line-through italic">"{oldText}"</p>
              <p className="text-sm text-slate-800 font-bold bg-emerald-50 p-3 rounded border border-emerald-100">
                <span className="text-emerald-600 mr-2">→</span>{newText}
              </p>
              {rationale && <p className="text-[10px] text-slate-500 italic mt-2">Coach Note: {rationale}</p>}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const ReportCard = ({ report, hideScoreHeader = false }) => {
  const { acceptMission, userProfile } = useAppContext();

  if (!report) return null;

  const { 
    overall_score = 0, 
    one_line_summary = "No summary available.", 
    ats_brief = "Layout analysis pending.",
    metric_analysis = [],
    cultural_translator = "Context analysis pending.",
    star_audit = [],
    authenticity_check = { cliches: [], fix: "No issues detected." },
    keyword_gap = { systems: [], missing_skills: [] },
    verb_pivot = { diagnosis: "Tone analysis pending.", driver_verbs: [] },
    positioning_shift = "Positioning recalibration pending.",
    regional_context = null,
    target_titles = []
  } = report;

  const getScoreColor = (score) => {
    if (score >= 80) return 'text-green-600';
    if (score >= 60) return 'text-yellow-600';
    return 'text-red-600';
  };

  return (
    <div className="mt-8 animate-fade-in">
      
      {!hideScoreHeader && (
          <div className="p-6 md:p-8 border border-gray-200 rounded-2xl shadow-lg bg-white relative overflow-hidden">
            <div className="absolute top-0 right-0 p-6 opacity-5 pointer-events-none"><Target size={120} /></div>
            <div className="flex flex-col md:flex-row items-center justify-between relative z-10">
              <div className="text-center md:text-left mb-4 md:mb-0">
                <h2 className="text-3xl font-black text-gray-900 tracking-tight">Resume Assessment</h2>
                <p className="text-gray-500 mt-1 max-w-sm">
                    This document has been graded against enterprise Applicant Tracking Systems (ATS) and Senior Recruiter logic patterns.
                </p>
              </div>
              <div className="text-center group relative">
                <div className={`text-6xl font-black ${getScoreColor(overall_score)}`}>{overall_score}</div>
                <div className="text-xs font-bold text-gray-500 uppercase tracking-wider mt-1 flex items-center justify-center gap-1 cursor-help">
                  ATS Viability Score
                  <HelpCircle size={14} className="text-gray-400" />
                </div>
                <div className="absolute top-full mt-2 right-0 md:left-1/2 md:-translate-x-1/2 w-64 p-3 bg-gray-900 text-white text-xs leading-relaxed rounded-xl shadow-xl opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-50">
                  ATS Viability is a predictive range. Different enterprise algorithms weigh keyword density differently. This score represents your median probability of bypassing the filters.
                </div>
              </div>
            </div>
          </div>
      )}

      {/* SENIOR MENTOR: STRATEGIC BRIEFING */}
      <div className={`${!hideScoreHeader ? 'mt-12 pt-8 border-t-2 border-slate-100' : ''} animate-fade-in`}>
        <div className="flex items-center gap-3 mb-8">
          <div className="p-3 bg-slate-900 rounded-2xl shadow-xl">
             <UserCheck className="text-emerald-400" size={24} />
          </div>
          <div>
            <h2 className="text-2xl font-black text-slate-900 uppercase tracking-tighter">Senior Mentor: Strategic Briefing</h2>
            <p className="text-slate-500 text-sm">Positioning Analysis & Market Calibration</p>
          </div>
        </div>

        {/* 1. THE POSITIONING SHIFT (Executive Summary) */}
        <div className="bg-indigo-900 text-white p-6 rounded-2xl shadow-lg mb-8 relative overflow-hidden">
          <div className="absolute top-0 right-0 p-4 opacity-10"><Rocket size={80} /></div>
          <h3 className="text-indigo-300 font-black uppercase text-xs tracking-widest mb-2">The Pivot</h3>
          <p className="text-lg leading-relaxed font-medium italic">"{positioning_shift}"</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {/* LEFT COLUMN: CRITICAL AUDITS */}
          <div className="space-y-6">
            <section>
              <h4 className="flex items-center font-black text-slate-800 uppercase text-xs tracking-widest mb-4">
                <Layers className="w-4 h-4 mr-2 text-blue-500" /> ATS & Layout Integrity
              </h4>
              <div className="p-4 bg-blue-50 rounded-xl border border-blue-100 text-sm text-blue-900 leading-relaxed">
                {ats_brief}
              </div>
            </section>

            {/* Metric Transformation */}
            <section>
              <h4 className="flex items-center font-black text-slate-800 uppercase text-xs tracking-widest mb-4">
                  <Target className="w-4 h-4 mr-2 text-emerald-500" /> Metric Transformation
              </h4>
              {metric_analysis?.map((item, i) => (
                  <ChangeCard key={`metric-${i}`} title="Impact Anchor" oldText={item.original} newText={item.suggested} rationale={item.rationale} />
              ))}
            </section>

            {/* STAR Evaluation */}
            <section className="mt-6">
              <h4 className="flex items-center font-black text-slate-800 uppercase text-xs tracking-widest mb-4">
                  <Star className="w-4 h-4 mr-2 text-yellow-500" /> STAR Evaluation
              </h4>
              {star_audit?.map((item, i) => (
                  <ChangeCard key={`star-${i}`} title="STAR Pivot" oldText={item.original} newText={item.suggested} rationale={item.rationale} />
              ))}
            </section>
          </div>

          {/* RIGHT COLUMN: MARKET CONTEXT */}
          <div className="space-y-6">
            <section className="p-5 bg-slate-50 rounded-2xl border border-slate-200">
               <h4 className="flex items-center font-black text-slate-800 uppercase text-xs tracking-widest mb-3">
                 <Zap className="w-4 h-4 mr-2 text-purple-500" /> Cultural Translation
               </h4>
               <p className="text-sm text-slate-600 leading-relaxed">{cultural_translator}</p>
            </section>

            <section className="p-5 bg-white rounded-2xl border border-orange-200 shadow-sm">
               <h4 className="flex items-center font-black text-orange-800 uppercase text-xs tracking-widest mb-3">
                 <ShieldCheck className="w-4 h-4 mr-2 text-orange-500" /> Authenticity Guard
               </h4>
               <p className="text-xs text-slate-500 mb-3 italic">"Detected 'ChatGPT-isms' that signal robotic writing:"</p>
               <div className="flex flex-wrap gap-2 mb-3">
                  {authenticity_check?.cliches?.map((word, i) => (
                      <span key={i} className="px-2 py-1 bg-orange-50 text-orange-700 text-[10px] font-bold rounded border border-orange-100">"{word}"</span>
                  ))}
               </div>
               <p className="text-xs font-bold text-orange-900">Mentor Fix: {authenticity_check?.fix}</p>
            </section>

            {regional_context && (
              <div className="flex items-start gap-3 p-4 bg-emerald-50 rounded-xl border border-emerald-100">
                 <MapPin className="text-emerald-600 mt-1 flex-shrink-0" size={18} />
                 <div>
                    <h5 className="text-[10px] font-black text-emerald-800 uppercase tracking-widest">Regional Advantage</h5>
                    <p className="text-sm text-emerald-900 leading-snug">{regional_context}</p>
                 </div>
              </div>
            )}
          </div>
        </div>

        {/* FOOTER: MARKET SEARCH LEADS */}
        <div className="mt-10 p-6 bg-slate-100 rounded-2xl flex flex-col sm:flex-row items-center justify-between gap-4">
           <div>
              <h4 className="font-black text-slate-800 text-sm uppercase">Recommended Search Targets</h4>
              <div className="flex gap-2 mt-2">
                 {target_titles?.map((title, i) => (
                   <span key={i} className="bg-white px-3 py-1 rounded-full text-xs font-bold text-blue-600 border border-blue-200 shadow-sm">{title}</span>
                 ))}
              </div>
           </div>
           <p className="text-[10px] text-slate-400 font-bold uppercase tracking-tighter max-w-[200px] text-center sm:text-right">
              Based on detected system expertise in {keyword_gap?.systems?.join(', ') || 'specialized tools'}.
           </p>
        </div>
      </div>
    </div>
  );
};

export default ReportCard;